import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { ChevronDown, ChevronRight, GripVertical, Users, Plus } from 'lucide-react';
import { cn } from '../lib/utils';
import {
  generateDayColumns,
  generateWeekColumns,
  calculateTaskPositionDays,
  calculateTaskPositionWeeks,
  DEFAULT_START_DATE,
  DEFAULT_END_DATE,
} from '../utils/dateUtils';
import { format, addDays, addWeeks, parseISO, differenceInDays, startOfWeek, isWithinInterval } from 'date-fns';

const CELL_WIDTH_DAY = 32;
const CELL_WIDTH_WEEK = 100;
const ROW_HEIGHT = 52;
const DEFAULT_SIDEBAR_WIDTH = 280;
const MIN_SIDEBAR_WIDTH = 200;
const MAX_SIDEBAR_WIDTH = 520;

export const GanttChart = ({ 
  projects, 
  tasks, 
  resources,
  viewMode,
  startDate,
  endDate,
  defaultExpandMode = 'expand',
  collapseSignal = 0,
  expandSignal = 0,
  onTaskClick,
  onProjectClick,
  onTaskUpdate,
  onTaskReorder,
  onProjectAddTask
}) => {
  const gridStartDate = startDate || DEFAULT_START_DATE;
  const gridEndDate = endDate || DEFAULT_END_DATE;

  const [expandedProjects, setExpandedProjects] = useState(function() {
    if (defaultExpandMode === 'collapse') {
      return new Set();
    }
    const set = new Set();
    for (let i = 0; i < projects.length; i++) {
      set.add(projects[i].id);
    }
    return set;
  });
  const projectIdsRef = useRef(new Set());

  useEffect(function() {
    setExpandedProjects(function() {
      if (defaultExpandMode === 'collapse') {
        return new Set();
      }
      const set = new Set();
      for (let i = 0; i < projects.length; i++) {
        set.add(projects[i].id);
      }
      return set;
    });
  }, [defaultExpandMode]);

  useEffect(function() {
    if (defaultExpandMode !== 'collapse') return;
    setExpandedProjects(new Set());
  }, [collapseSignal, defaultExpandMode]);

  useEffect(function() {
    if (defaultExpandMode !== 'expand') return;
    const set = new Set();
    for (let i = 0; i < projects.length; i++) {
      set.add(projects[i].id);
    }
    setExpandedProjects(set);
  }, [expandSignal, defaultExpandMode, projects]);

  useEffect(function() {
    const currentIds = new Set();
    for (let i = 0; i < projects.length; i++) {
      currentIds.add(projects[i].id);
    }

    setExpandedProjects(function(prev) {
      const next = new Set();
      for (const id of prev) {
        if (currentIds.has(id)) {
          next.add(id);
        }
      }
      if (defaultExpandMode === 'expand') {
        for (const id of currentIds) {
          if (!projectIdsRef.current.has(id)) {
            next.add(id);
          }
        }
      }
      return next;
    });

    projectIdsRef.current = currentIds;
  }, [projects, defaultExpandMode]);
  
  // Drag state
  const [dragState, setDragState] = useState(null);
  const [taskDragState, setTaskDragState] = useState(null);
  const [taskDragOver, setTaskDragOver] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(DEFAULT_SIDEBAR_WIDTH);
  const resizeStateRef = useRef(null);
  
  const sidebarRef = useRef(null);
  const headerRef = useRef(null);
  const bodyRef = useRef(null);
  const isSyncingScrollRef = useRef(false);

  const cellWidth = viewMode === 'day' ? CELL_WIDTH_DAY : CELL_WIDTH_WEEK;

  // Resource map for quick lookup
  const resourceMap = useMemo(function() {
    const map = {};
    for (let i = 0; i < resources.length; i++) {
      map[resources[i].id] = resources[i];
    }
    return map;
  }, [resources]);

  const columns = useMemo(function() {
    if (viewMode === 'day') {
      return generateDayColumns(gridStartDate, gridEndDate);
    }
    return generateWeekColumns(gridStartDate, gridEndDate);
  }, [viewMode, gridStartDate, gridEndDate]);

  const totalWidth = viewMode === 'day' 
    ? columns.totalDays * cellWidth 
    : columns.totalWeeks * cellWidth;

  const todayLineLeft = useMemo(function() {
    const today = new Date();
    if (viewMode === 'day') {
      const gridStart = parseISO(gridStartDate);
      const gridEnd = parseISO(gridEndDate);
      if (!isWithinInterval(today, { start: gridStart, end: gridEnd })) return null;
      const offsetDays = differenceInDays(today, gridStart);
      return offsetDays * cellWidth + Math.floor(cellWidth / 2);
    }

    const gridStartWeek = startOfWeek(parseISO(gridStartDate), { weekStartsOn: 1 });
    const gridEndWeek = startOfWeek(parseISO(gridEndDate), { weekStartsOn: 1 });
    const todayWeek = startOfWeek(today, { weekStartsOn: 1 });
    if (todayWeek < gridStartWeek || todayWeek > gridEndWeek) return null;
    const offsetWeeks = Math.floor(differenceInDays(todayWeek, gridStartWeek) / 7);
    return offsetWeeks * cellWidth + Math.floor(cellWidth / 2);
  }, [viewMode, gridStartDate, gridEndDate, cellWidth]);

  // Sync horizontal scroll between header and body
  const handleBodyScroll = function(e) {
    if (isSyncingScrollRef.current) return;
    isSyncingScrollRef.current = true;
    const scrollLeft = e.target.scrollLeft;
    const scrollTop = e.target.scrollTop;
    
    if (headerRef.current) {
      headerRef.current.scrollLeft = scrollLeft;
    }
    if (sidebarRef.current) {
      sidebarRef.current.scrollTop = scrollTop;
    }
    requestAnimationFrame(function() {
      isSyncingScrollRef.current = false;
    });
  };

  const handleSidebarScroll = function(e) {
    if (isSyncingScrollRef.current) return;
    isSyncingScrollRef.current = true;
    if (bodyRef.current) {
      bodyRef.current.scrollTop = e.target.scrollTop;
    }
    requestAnimationFrame(function() {
      isSyncingScrollRef.current = false;
    });
  };

  const toggleProject = function(projectId) {
    setExpandedProjects(function(prev) {
      const next = new Set(prev);
      if (next.has(projectId)) {
        next.delete(projectId);
      } else {
        next.add(projectId);
      }
      return next;
    });
  };

  const taskOrderIndex = useMemo(function() {
    const map = {};
    for (let i = 0; i < tasks.length; i++) {
      map[tasks[i].id] = i;
    }
    return map;
  }, [tasks]);

  const tasksByProject = useMemo(function() {
    const map = {};
    for (let i = 0; i < tasks.length; i++) {
      const task = tasks[i];
      if (!map[task.projectId]) {
        map[task.projectId] = [];
      }
      map[task.projectId].push(task);
    }
    const projectIds = Object.keys(map);
    for (let i = 0; i < projectIds.length; i++) {
      const projectId = projectIds[i];
      map[projectId].sort(function(a, b) {
        const aOrder = typeof a.sortOrder === 'number' ? a.sortOrder : null;
        const bOrder = typeof b.sortOrder === 'number' ? b.sortOrder : null;
        if (aOrder !== null && bOrder !== null) return aOrder - bOrder;
        if (aOrder !== null) return -1;
        if (bOrder !== null) return 1;
        return (taskOrderIndex[a.id] || 0) - (taskOrderIndex[b.id] || 0);
      });
    }
    return map;
  }, [tasks, taskOrderIndex]);

  const handleTaskRowDragStart = useCallback(function(e, task, projectId) {
    if (!onTaskReorder) return;
    e.stopPropagation();
    setTaskDragState({ taskId: task.id, projectId: projectId });
    try {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', task.id);
    } catch (err) {
      // Ignore dataTransfer issues in some browsers
    }
  }, [onTaskReorder]);

  const handleTaskRowDragOver = useCallback(function(e, task, projectId) {
    if (!taskDragState || taskDragState.projectId !== projectId) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const position = (e.clientY - rect.top) > rect.height / 2 ? 'after' : 'before';
    setTaskDragOver({ taskId: task.id, position: position });
  }, [taskDragState]);

  const handleTaskRowDrop = useCallback(function(e, targetTask, projectId) {
    if (!taskDragState || taskDragState.projectId !== projectId) return;
    e.preventDefault();
    const rect = e.currentTarget.getBoundingClientRect();
    const position = (e.clientY - rect.top) > rect.height / 2 ? 'after' : 'before';
    const projectTasks = tasksByProject[projectId] || [];
    const ids = projectTasks.map(function(t) { return t.id; });
    if (ids.indexOf(taskDragState.taskId) === -1 || ids.indexOf(targetTask.id) === -1) return;
    if (taskDragState.taskId === targetTask.id) return;

    const reordered = ids.filter(function(id) { return id !== taskDragState.taskId; });
    const targetIndex = reordered.indexOf(targetTask.id);
    const insertIndex = position === 'after' ? targetIndex + 1 : targetIndex;
    reordered.splice(insertIndex, 0, taskDragState.taskId);

    const taskMap = {};
    for (let i = 0; i < projectTasks.length; i++) {
      taskMap[projectTasks[i].id] = projectTasks[i];
    }
    const updatedTasks = reordered.map(function(id, index) {
      const original = taskMap[id];
      return original ? { ...original, sortOrder: index } : null;
    }).filter(Boolean);

    if (onTaskReorder) onTaskReorder(updatedTasks);
    setTaskDragState(null);
    setTaskDragOver(null);
  }, [taskDragState, tasksByProject, onTaskReorder]);

  const handleTaskRowDragEnd = useCallback(function() {
    setTaskDragState(null);
    setTaskDragOver(null);
  }, []);

  const rows = useMemo(function() {
    const result = [];
    for (let p = 0; p < projects.length; p++) {
      const project = projects[p];
      result.push({ type: 'project', data: project });
      if (expandedProjects.has(project.id)) {
        const projectTasks = tasksByProject[project.id] || [];
        for (let t = 0; t < projectTasks.length; t++) {
          const task = projectTasks[t];
          result.push({ type: 'task', data: task, project: project });
        }
      }
    }
    return result;
  }, [projects, tasksByProject, expandedProjects]);

  const getProgressColor = function(progress) {
    if (progress >= 80) return 'bg-emerald-500';
    if (progress >= 50) return 'bg-amber-500';
    if (progress >= 20) return 'bg-orange-500';
    return 'bg-slate-400';
  };

  // Calculate average progress for a project
  const getProjectProgress = function(projectId) {
    const projectTasks = tasks.filter(function(t) { return t.projectId === projectId; });
    if (projectTasks.length === 0) return 0;
    const total = projectTasks.reduce(function(acc, t) { return acc + t.progress; }, 0);
    return Math.round(total / projectTasks.length);
  };

  // Drag handlers for task repositioning
  const handleDragStart = useCallback(function(e, task, dragType) {
    e.stopPropagation();
    e.preventDefault();
    
    const rect = e.currentTarget.closest('.task-bar-container').getBoundingClientRect();
    const bodyRect = bodyRef.current.getBoundingClientRect();
    
    setDragState({
      task: task,
      dragType: dragType, // 'move', 'resize-left', 'resize-right'
      startX: e.clientX,
      startOffset: rect.left - bodyRect.left + bodyRef.current.scrollLeft,
      originalStart: task.startDate,
      originalEnd: task.endDate
    });
    
    document.body.style.cursor = dragType === 'move' ? 'grabbing' : 'ew-resize';
  }, []);

  const handleDragMove = useCallback(function(e) {
    if (!dragState) return;
    
    const deltaX = e.clientX - dragState.startX;
    const cellsDelta = Math.round(deltaX / cellWidth);
    
    if (cellsDelta === 0) return;
    
    const task = dragState.task;
    const baseStart = parseISO(dragState.originalStart);
    const baseEnd = parseISO(dragState.originalEnd);
    
    let newStart, newEnd;
    
    if (viewMode === 'day') {
      if (dragState.dragType === 'move') {
        newStart = addDays(baseStart, cellsDelta);
        newEnd = addDays(baseEnd, cellsDelta);
      } else if (dragState.dragType === 'resize-left') {
        newStart = addDays(baseStart, cellsDelta);
        newEnd = baseEnd;
        if (newStart >= newEnd) return;
      } else if (dragState.dragType === 'resize-right') {
        newStart = baseStart;
        newEnd = addDays(baseEnd, cellsDelta);
        if (newEnd <= newStart) return;
      }
    } else {
      // Week mode - move by weeks
      if (dragState.dragType === 'move') {
        newStart = addWeeks(baseStart, cellsDelta);
        newEnd = addWeeks(baseEnd, cellsDelta);
      } else if (dragState.dragType === 'resize-left') {
        newStart = addWeeks(baseStart, cellsDelta);
        newEnd = baseEnd;
        if (newStart >= newEnd) return;
      } else if (dragState.dragType === 'resize-right') {
        newStart = baseStart;
        newEnd = addWeeks(baseEnd, cellsDelta);
        if (newEnd <= newStart) return;
      }
    }
    
    // Update task dates through callback
    if (onTaskUpdate) {
      onTaskUpdate({
        ...task,
        startDate: format(newStart, 'yyyy-MM-dd'),
        endDate: format(newEnd, 'yyyy-MM-dd')
      });
    }
  }, [dragState, cellWidth, viewMode, onTaskUpdate]);

  const handleDragEnd = useCallback(function() {
    if (dragState) {
      document.body.style.cursor = '';
      setDragState(null);
    }
  }, [dragState]);

  const handleResizeStart = useCallback(function(e) {
    e.preventDefault();
    resizeStateRef.current = {
      startX: e.clientX,
      startWidth: sidebarWidth
    };
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  }, [sidebarWidth]);

  useEffect(function() {
    const onMove = function(e) {
      if (!resizeStateRef.current) return;
      const delta = e.clientX - resizeStateRef.current.startX;
      const next = resizeStateRef.current.startWidth + delta;
      const clamped = Math.max(MIN_SIDEBAR_WIDTH, Math.min(MAX_SIDEBAR_WIDTH, next));
      setSidebarWidth(clamped);
    };
    const onUp = function() {
      if (!resizeStateRef.current) return;
      resizeStateRef.current = null;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return function() {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Add global mouse listeners for drag
  useEffect(function() {
    if (dragState) {
      const onMove = function(e) { handleDragMove(e); };
      const onUp = function() { handleDragEnd(); };
      
      window.addEventListener('mousemove', onMove);
      window.addEventListener('mouseup', onUp);
      
      return function() {
        window.removeEventListener('mousemove', onMove);
        window.removeEventListener('mouseup', onUp);
      };
    }
  }, [dragState, handleDragMove, handleDragEnd]);

  // Get resource names for a task
  const getTaskResourceNames = function(task) {
    if (!task.resourceAllocations || task.resourceAllocations.length === 0) {
      return [];
    }
    const names = [];
    for (let i = 0; i < task.resourceAllocations.length; i++) {
      const allocation = task.resourceAllocations[i];
      const resource = resourceMap[allocation.resourceId];
      if (resource) {
        names.push(resource.name.split(' ')[0]); // First name only
      }
    }
    return names;
  };

  // Build month headers
  const monthHeaders = [];
  for (let i = 0; i < columns.months.length; i++) {
    const month = columns.months[i];
    const span = viewMode === 'day' ? month.days.length : month.weeks.length;
    monthHeaders.push(
      <div 
        key={'month-' + i}
        className="text-xs font-semibold text-muted-foreground uppercase tracking-wider py-2 text-center border-r border-border/30 bg-muted/50 flex-shrink-0"
        style={{ width: span * cellWidth }}
      >
        {month.key}
      </div>
    );
  }

  // Build day/week headers
  const subHeaders = [];
  if (viewMode === 'day') {
    for (let m = 0; m < columns.months.length; m++) {
      const month = columns.months[m];
      for (let d = 0; d < month.days.length; d++) {
        const day = month.days[d];
        subHeaders.push(
          <div 
            key={'day-' + m + '-' + d}
            className={cn(
              "text-xs py-2 text-center border-r border-border/20 flex-shrink-0",
              day.isWeekend && "bg-muted/60 text-muted-foreground",
              day.isFirstOfMonth && "border-l-2 border-l-border"
            )}
            style={{ width: cellWidth }}
          >
            {day.label}
          </div>
        );
      }
    }
  } else {
    for (let m = 0; m < columns.months.length; m++) {
      const month = columns.months[m];
      for (let w = 0; w < month.weeks.length; w++) {
        const week = month.weeks[w];
        const labelParts = week.label.split('-');
        subHeaders.push(
          <div 
            key={'week-' + m + '-' + w}
            className="text-xs py-2 text-center border-r border-border/20 truncate px-1 flex-shrink-0"
            style={{ width: cellWidth }}
            title={week.label}
          >
            {labelParts[0]}
          </div>
        );
      }
    }
  }

  // Build sidebar rows
  const sidebarRows = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    if (row.type === 'project') {
      sidebarRows.push(
        <div 
          key={'sidebar-p-' + row.data.id + '-' + i}
          className="flex items-center px-3 border-b border-border/50 cursor-pointer transition-colors bg-muted/30 hover:bg-muted/50 font-medium"
          style={{ height: ROW_HEIGHT }}
          onClick={function() { if (onProjectClick) onProjectClick(row.data); }}
        >
          <button 
            onClick={function(e) { e.stopPropagation(); toggleProject(row.data.id); }}
            className="p-1 hover:bg-muted rounded mr-2"
            data-testid={'toggle-project-' + row.data.id}
          >
            {expandedProjects.has(row.data.id) ? (
              <ChevronDown className="w-4 h-4" />
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
          <div 
            className="w-3 h-3 rounded-full mr-2 flex-shrink-0"
            style={{ backgroundColor: row.data.color }}
          />
          <span className="truncate text-sm flex-1 min-w-0" data-testid={'project-name-' + row.data.id}>
            {row.data.name}
          </span>
          {onProjectAddTask && (
            <button
              type="button"
              onClick={function(e) {
                e.stopPropagation();
                onProjectAddTask(row.data);
              }}
              className="ml-2 p-1 rounded hover:bg-muted/60"
              aria-label="Crea task"
              title="Crea task"
              data-testid={'project-add-task-' + row.data.id}
            >
              <Plus className="w-4 h-4" />
            </button>
          )}
        </div>
      );
    } else {
      const taskResourceNames = getTaskResourceNames(row.data);
      sidebarRows.push(
        <div 
          key={'sidebar-t-' + row.data.id + '-' + i}
          className={cn(
            "flex items-center px-3 border-b border-border/50 cursor-pointer transition-colors hover:bg-muted/20 pl-8",
            taskDragOver && taskDragOver.taskId === row.data.id && taskDragOver.position === 'before' && "border-t-2 border-t-primary",
            taskDragOver && taskDragOver.taskId === row.data.id && taskDragOver.position === 'after' && "border-b-2 border-b-primary"
          )}
          style={{ height: ROW_HEIGHT }}
          onClick={function() { if (onTaskClick) onTaskClick(row.data); }}
          draggable={!!onTaskReorder}
          onDragStart={function(e) { handleTaskRowDragStart(e, row.data, row.project.id); }}
          onDragOver={function(e) { handleTaskRowDragOver(e, row.data, row.project.id); }}
          onDrop={function(e) { handleTaskRowDrop(e, row.data, row.project.id); }}
          onDragEnd={handleTaskRowDragEnd}
        >
          <GripVertical className={cn(
            "w-4 h-4 text-muted-foreground mr-2 flex-shrink-0",
            onTaskReorder ? "cursor-grab" : "cursor-default"
          )} />
          <div className="flex-1 min-w-0">
            <span className="truncate text-sm block" data-testid={'task-name-' + row.data.id}>
              {row.data.name}
            </span>
            {taskResourceNames.length > 0 && (
              <div className="flex items-center gap-1 mt-0.5">
                <Users className="w-3 h-3 text-muted-foreground" />
                <span className="text-xs text-muted-foreground truncate">
                  {taskResourceNames.join(', ')}
                </span>
              </div>
            )}
          </div>
          <span className="ml-2 text-xs text-muted-foreground flex-shrink-0">
            {row.data.progress}%
          </span>
        </div>
      );
    }
  }

  // Build grid background cells
  const buildGridCells = function() {
    const cells = [];
    if (viewMode === 'day') {
      for (let m = 0; m < columns.months.length; m++) {
        const month = columns.months[m];
        for (let d = 0; d < month.days.length; d++) {
          const day = month.days[d];
          cells.push(
            <div 
              key={'grid-' + m + '-' + d}
              className={cn(
                "border-r border-border/10 flex-shrink-0",
                day.isWeekend && "bg-muted/30",
                day.isFirstOfMonth && "border-l border-l-border/50"
              )}
              style={{ width: cellWidth, height: '100%' }}
            />
          );
        }
      }
    } else {
      for (let i = 0; i < columns.totalWeeks; i++) {
        cells.push(
          <div 
            key={'grid-w-' + i}
            className="border-r border-border/10 flex-shrink-0"
            style={{ width: cellWidth, height: '100%' }}
          />
        );
      }
    }
    return cells;
  };

  // Build timeline rows
  const timelineRows = [];
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const isProject = row.type === 'project';
    
    let barElement = null;
    
    if (isProject) {
      // Project bar - spans the entire project duration
      const position = viewMode === 'day' 
        ? calculateTaskPositionDays(row.data.startDate, row.data.endDate, gridStartDate)
        : calculateTaskPositionWeeks(row.data.startDate, row.data.endDate, gridStartDate);
      
      const barLeft = position.startOffset * cellWidth + 4;
      const barWidth = Math.max(position.duration * cellWidth - 8, 20);
      const projectProgress = getProjectProgress(row.data.id);
      
      barElement = (
        <div 
          className="absolute top-3 bottom-3 rounded-md overflow-hidden cursor-pointer transition-transform hover:scale-[1.02] hover:shadow-lg border-2"
          style={{ 
            left: barLeft,
            width: barWidth,
            backgroundColor: row.data.color + '30',
            borderColor: row.data.color
          }}
          onClick={function() { if (onProjectClick) onProjectClick(row.data); }}
          data-testid={'project-bar-' + row.data.id}
          title={row.data.name + ' (' + projectProgress + '% completato)'}
        >
          {/* Progress fill */}
          <div 
            className="h-full opacity-60"
            style={{ width: projectProgress + '%', backgroundColor: row.data.color }}
          />
          {/* Progress label */}
          {barWidth > 50 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-semibold text-foreground drop-shadow-sm">
                {projectProgress}%
              </span>
            </div>
          )}
        </div>
      );
    } else {
      // Task bar with drag & drop
      const position = viewMode === 'day' 
        ? calculateTaskPositionDays(row.data.startDate, row.data.endDate, gridStartDate)
        : calculateTaskPositionWeeks(row.data.startDate, row.data.endDate, gridStartDate);
      
      const barLeft = position.startOffset * cellWidth + 4;
      const barWidth = Math.max(position.duration * cellWidth - 8, 40);
      const taskResourceNames = getTaskResourceNames(row.data);
      const isDragging = dragState && dragState.task.id === row.data.id;
      
      barElement = (
        <div 
          className={cn(
            "absolute top-2 bottom-2 rounded-lg overflow-hidden cursor-pointer transition-all task-bar-container group",
            isDragging && "opacity-70 shadow-xl z-10",
            !isDragging && "hover:shadow-lg"
          )}
          style={{ 
            left: barLeft,
            width: barWidth,
            backgroundColor: row.project.color,
            opacity: isDragging ? 0.7 : 0.9
          }}
          data-testid={'task-bar-' + row.data.id}
        >
          {/* Left resize handle */}
          {onTaskUpdate && (
            <div 
              className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 bg-black/20 hover:bg-black/40 transition-opacity z-20"
              onMouseDown={function(e) { handleDragStart(e, row.data, 'resize-left'); }}
            />
          )}
          
          {/* Progress background */}
          <div 
            className={cn("h-full", getProgressColor(row.data.progress))}
            style={{ width: row.data.progress + '%' }}
          />
          
          {/* Task content overlay */}
          <div 
            className="absolute inset-0 flex items-center justify-between px-2 cursor-grab active:cursor-grabbing"
            onClick={function() { if (onTaskClick && !isDragging) onTaskClick(row.data); }}
            onMouseDown={onTaskUpdate ? function(e) { handleDragStart(e, row.data, 'move'); } : undefined}
          >
            {/* Resource avatars on bar */}
            {taskResourceNames.length > 0 && barWidth > 80 && (
              <div className="flex items-center gap-0.5 mr-1">
                {taskResourceNames.slice(0, 3).map(function(name, idx) {
                  return (
                    <div 
                      key={idx}
                      className="w-5 h-5 rounded-full bg-white/90 flex items-center justify-center text-xs font-medium shadow-sm"
                      style={{ color: row.project.color }}
                      title={name}
                    >
                      {name.charAt(0)}
                    </div>
                  );
                })}
                {taskResourceNames.length > 3 && (
                  <div className="w-5 h-5 rounded-full bg-white/70 flex items-center justify-center text-xs font-medium">
                    +{taskResourceNames.length - 3}
                  </div>
                )}
              </div>
            )}
            
            {/* Progress text */}
            {barWidth > 50 && (
              <span className="text-xs font-medium text-white drop-shadow-sm ml-auto">
                {row.data.progress}%
              </span>
            )}
          </div>
          
          {/* Right resize handle */}
          {onTaskUpdate && (
            <div 
              className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize opacity-0 group-hover:opacity-100 bg-black/20 hover:bg-black/40 transition-opacity z-20"
              onMouseDown={function(e) { handleDragStart(e, row.data, 'resize-right'); }}
            />
          )}
        </div>
      );
    }

    timelineRows.push(
      <div 
        key={isProject ? 'timeline-pg-' + row.data.id + '-' + i : 'timeline-tg-' + row.data.id + '-' + i}
        className={cn(
          "relative border-b border-border/30 flex",
          isProject && "bg-muted/20"
        )}
        style={{ height: ROW_HEIGHT, width: totalWidth }}
      >
        {buildGridCells()}
        {barElement}
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full border border-border rounded-lg overflow-hidden bg-card">
      {/* Header */}
      <div className="flex border-b border-border bg-muted/30 flex-shrink-0">
        <div 
          className="flex-shrink-0 border-r border-border px-4 py-3 font-semibold text-sm"
          style={{ width: sidebarWidth }}
        >
          Progetti / Task
        </div>

        <div
          className="w-2 cursor-col-resize bg-border/40 hover:bg-border"
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Ridimensiona colonna progetti"
        />
        
        {/* Header timeline - synced with body */}
        <div 
          ref={headerRef}
          className="flex-1 overflow-hidden"
          style={{ overflowX: 'hidden' }}
        >
          <div style={{ width: totalWidth }}>
            <div className="flex border-b border-border/50">
              {monthHeaders}
            </div>
            <div className="flex">
              {subHeaders}
            </div>
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - synced vertical scroll */}
        <div 
          ref={sidebarRef}
          className="flex-shrink-0 border-r border-border overflow-auto"
          style={{ width: sidebarWidth }}
          onScroll={handleSidebarScroll}
        >
          <div>
            {sidebarRows}
          </div>
        </div>

        <div
          className="w-2 cursor-col-resize bg-border/40 hover:bg-border"
          onMouseDown={handleResizeStart}
          role="separator"
          aria-orientation="vertical"
          aria-label="Ridimensiona colonna progetti"
        />

        {/* Main scrollable area */}
        <div 
          ref={bodyRef}
          className="flex-1 overflow-auto"
          onScroll={handleBodyScroll}
        >
          <div style={{ width: totalWidth, minHeight: '100%', position: 'relative' }}>
            {todayLineLeft !== null && (
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 pointer-events-none z-20"
                style={{ left: todayLineLeft }}
              />
            )}
            {timelineRows}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GanttChart;
